import type { DeploymentResponse } from "@nexus/types"
import { createSelector } from "@reduxjs/toolkit"

import type { EmbedChatState } from "./embedTypes"

export interface EmbedRootState {
  embedChat: EmbedChatState
}

/**
 * Select embed chat by ID
 */
export const selectEmbedChat = (chatId: string) => (state: EmbedRootState) =>
  state.embedChat.chats[chatId]

/**
 * Select active embed chat ID
 */
export const selectActiveEmbedChatId = (state: EmbedRootState) => state.embedChat.activeChatId

/**
 * Select active embed chat
 */
export const selectActiveEmbedChat = (state: EmbedRootState) => {
  const activeChatId = selectActiveEmbedChatId(state)
  return activeChatId ? state.embedChat.chats[activeChatId] : null
}

/**
 * Select all embed chats
 */
export const selectAllEmbedChats = (state: EmbedRootState) => state.embedChat.chats

/**
 * Select embed chat messages
 */
export const selectEmbedChatMessages = (chatId: string) => (state: EmbedRootState) => {
  const chat = state.embedChat.chats[chatId]
  return chat?.messages || []
}

/**
 * Select embed chat streaming messages
 * Returns a NEW object reference to trigger React re-renders on every token
 */
export const selectEmbedStreamingMessages = (chatId: string) => (state: EmbedRootState) => {
  const chat = state.embedChat.chats[chatId]
  const streaming = chat?.streamingMessages || {}
  // Return a new object reference to force React re-renders
  // Without this, Immer's same-reference optimization prevents UI updates during streaming
  return { ...streaming }
}

/**
 * Select embed chat init phase
 */
export const selectEmbedInitPhase = (chatId: string) => (state: EmbedRootState) => {
  const chat = state.embedChat.chats[chatId]
  return chat?.initializationPhase || "idle"
}

/**
 * Select embed chat is loading
 */
export const selectEmbedIsLoading = (chatId: string) => (state: EmbedRootState) => {
  const chat = state.embedChat.chats[chatId]
  return chat?.isLoadingMessages || chat?.isSendingMessage || false
}

/**
 * Select embed chat deployment (same shape as API).
 */
export const selectEmbedDeployment =
  (chatId: string) =>
  (state: EmbedRootState): DeploymentResponse | undefined => {
    const chat = state.embedChat.chats[chatId]
    return chat?.deployment
  }

/**
 * Select embed chat deployment (alias for backward compatibility).
 * @deprecated Use selectEmbedDeployment instead
 */
export const selectEmbedIntegration =
  (chatId: string) =>
  (state: EmbedRootState): DeploymentResponse | undefined => {
    return selectEmbedDeployment(chatId)(state)
  }

/**
 * Select embed chat integration metadata (for backward compatibility)
 * @deprecated Use selectEmbedDeployment instead
 */
export const selectEmbedIntegrationMetadata =
  (chatId: string) =>
  (state: EmbedRootState): DeploymentResponse | undefined => {
    return selectEmbedDeployment(chatId)(state)
  }

/**
 * Select embed chat settings (embedSettings from deployment for EMBED type).
 */
export const selectEmbedChatSettings = (chatId: string) => (state: EmbedRootState) => {
  const deployment = state.embedChat.chats[chatId]?.deployment
  if (!deployment || deployment.type !== "EMBED" || !deployment.deploymentSettings) return undefined
  const settings = deployment.deploymentSettings as { embedSettings?: unknown }
  return settings.embedSettings
}

/**
 * Select embed chat agent (agent from deployment).
 */
export const selectEmbedChatAgent = (chatId: string) => (state: EmbedRootState) => {
  const chat = state.embedChat.chats[chatId]
  return chat?.deployment?.agent
}

/**
 * Select active embed chat messages (memoized)
 */
export const selectActiveEmbedChatMessages = createSelector(
  [selectActiveEmbedChat],
  (chat) => chat?.messages || []
)

/**
 * Select active embed streaming messages (memoized)
 */
export const selectActiveEmbedStreamingMessages = createSelector(
  [selectActiveEmbedChat],
  (chat) => chat?.streamingMessages || {}
)

/**
 * Select if embed chat is ready
 */
export const selectEmbedIsReady = (chatId: string) => (state: EmbedRootState) => {
  const chat = state.embedChat.chats[chatId]
  return chat?.initializationPhase === "ready"
}

/**
 * Select embed chat error
 */
export const selectEmbedError = (chatId: string) => (state: EmbedRootState) => {
  const chat = state.embedChat.chats[chatId]
  return chat?.initializationError
}
